import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Search, Check, X, Trash2 } from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentQuotes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, [filter]);

  const fetchQuotes = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const response = await axios.get(`${API}/agent/quotes${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/agent/quotes/${quoteId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Quote ${status}`);
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to update quote status");
    }
  };

  const handleDeleteClick = (quote) => {
    setQuoteToDelete(quote);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!quoteToDelete) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/quotes/${quoteToDelete.quote_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote deleted successfully");
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      expired: "bg-gray-100 text-gray-800",
      converted: "bg-blue-100 text-blue-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const filteredQuotes = quotes.filter(quote => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      quote.quote_id.toLowerCase().includes(searchLower) ||
      quote.user?.email?.toLowerCase().includes(searchLower) ||
      quote.user?.full_name?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <AgentLayout title="Quotes">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          Quote Management
        </h1>
        <p className="text-muted-foreground">Review and approve customer quotes</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search quotes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="quote-search-input"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="quote-filter-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Quotes</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Quotes Table */}
      <Card className="card-organic overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredQuotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Quote ID</th>
                    <th>Customer</th>
                    <th>Monthly Rent</th>
                    <th>Premium</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.quote_id}>
                      <td className="font-mono text-sm">{quote.quote_id}</td>
                      <td>
                        <div>
                          <p className="font-medium">{quote.user?.full_name || "N/A"}</p>
                          <p className="text-sm text-muted-foreground">{quote.user?.email}</p>
                        </div>
                      </td>
                      <td>${quote.monthly_rent}</td>
                      <td>${quote.monthly_premium}/mo</td>
                      <td>
                        <Badge className={getStatusBadge(quote.status)}>
                          {quote.status}
                        </Badge>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          {quote.status === "pending" && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => updateQuoteStatus(quote.quote_id, "approved")}
                                data-testid={`approve-quote-${quote.quote_id}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => updateQuoteStatus(quote.quote_id, "rejected")}
                                data-testid={`reject-quote-${quote.quote_id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {quote.status !== "converted" && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteClick(quote)}
                              data-testid={`delete-quote-${quote.quote_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          {quote.status === "converted" && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => navigate(`/agent/customers/${quote.user_id}`)}
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No quotes found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Quote</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this quote? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {quoteToDelete && (
            <div className="py-4 space-y-2">
              <p><strong>Quote ID:</strong> {quoteToDelete.quote_id}</p>
              <p><strong>Customer:</strong> {quoteToDelete.user?.full_name || "N/A"}</p>
              <p><strong>Monthly Rent:</strong> ${quoteToDelete.monthly_rent}</p>
              <p><strong>Status:</strong> {quoteToDelete.status}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              data-testid="confirm-delete-quote"
            >
              Delete Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
